import React, { useEffect } from "react"
import PropTypes from "prop-types"
import Loadable from "react-loadable"
import { compose } from "redux"

import { Switch, Route, Redirect } from "react-router-dom"

import { loadingAnimationVariants } from "constants/internal"
import { registerForm } from "constants/uri"
import withErrorBoundary from "lib/utils/hoc/withErrorBoundary"
import withLanguage from "lib/utils/hoc/withLanguage"

import Loading from "lib/views/Loading/Loading"
import EnvironmentWrapper from "App/EnvironmentWrapper"
import CreateUser from "App/components/CreateUser"
import PolicyAgreementDialog from "App/components/PolicyAgreementDialog"

import connect from "./connect"

const NotFound = Loadable({
    loader: () =>
        import(/* webpackChunkName: "not-found" */ "lib/views/NotFound"),
    loading: () => <Loading variant={loadingAnimationVariants.secondary} />,
})
const LoginRedirect = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "login-redirect" */ "App/components/LoginRedirect"
        ),
    loading: () => <Loading variant={loadingAnimationVariants.secondary} />,
})
const NoActiveRoles = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "inactive-user" */ "App/components/NoActiveRoles"
        ),
    loading: () => <Loading variant={loadingAnimationVariants.secondary} />,
})
const RouteLogin = Loadable({
    loader: () =>
        import(/* webpackChunkName: "login" */ "App/components/Login"),
    loading: () => <Loading variant={loadingAnimationVariants.secondary} />,
})
const ForgotPassword = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "forgot-password" */ "App/components/ForgotPassword"
        ),
    loading: () => <Loading variant={loadingAnimationVariants.secondary} />,
})
const Roles = Loadable({
    loader: () =>
        import(/* webpackChunkName: "roles" */ "App/components/Roles"),
    loading: () => <Loading variant={loadingAnimationVariants.secondary} />,
})
const RoutesRegister = Loadable({
    loader: () =>
        import(/* webpackChunkName: "register" */ "App/RoutesRegister"),
    loading: () => <Loading variant={loadingAnimationVariants.secondary} />,
})
const RoutesIntegration = Loadable({
    loader: () =>
        import(
            /* webpackChunkName: "integrationsRedirects" */ "App/RoutesIntegration"
        ),
    loading: () => <Loading variant={loadingAnimationVariants.secondary} />,
})

export const App = ({
    isLoggedIn,
    redirectUrl,
    hasConfirmedEmail,
    hasCompany,
    loadInvites,
    invites,
    hasFetchedInvites,
    isSubscribed,
    hasActiveRoles,
    hasFetchedBillingInfo,
    hasFetchedLoggedInUser,
    isLoadingUserInvites,
    isAdmin,
}) => {
    const hasInvites = hasFetchedInvites && !!invites.length

    useEffect(() => {
        if (isLoggedIn && !hasFetchedInvites) loadInvites()
    }, [isLoggedIn, hasFetchedInvites])

    if (!isLoggedIn) {
        return (
            <Switch>
                <Route exact path="/" component={RouteLogin} />
                <Route exact path="/login" component={RouteLogin} />
                <Route
                    exact
                    path="/forgot-password/:resetToken?"
                    component={ForgotPassword}
                />
                <Route
                    path="/register/:layer?/:activationKey?"
                    component={RoutesRegister}
                />
                <Route component={LoginRedirect} />
            </Switch>
        )
    }

    if (
        hasConfirmedEmail &&
        hasFetchedLoggedInUser &&
        hasCompany &&
        !hasActiveRoles &&
        !hasInvites &&
        !isLoadingUserInvites
    ) {
        return <Route component={NoActiveRoles} />
    }

    if (
        !hasConfirmedEmail ||
        (!hasCompany && !hasInvites) ||
        (hasCompany && hasFetchedBillingInfo && !isSubscribed && isAdmin)
    ) {
        return (
            <React.Fragment>
                <Switch>
                    <Route
                        path="/register/:layer?/:activationKey?"
                        component={RoutesRegister}
                    />
                    <Route render={() => <Redirect to="/register" />} />
                </Switch>

                <PolicyAgreementDialog />
            </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            <Switch>
                {/* Redirect to the users previous location after login */}
                <Route
                    exact
                    path="/login"
                    render={() => <Redirect to={redirectUrl} />}
                />
                <Route exact path="/roles" component={Roles} />
                <Route
                    path={`/register/${registerForm.user}/:inviteHash?`}
                    component={CreateUser}
                />
                <Route
                    path="/editor/settings/integration/:integration?"
                    component={RoutesIntegration}
                />

                <Route path="/:environment?" component={EnvironmentWrapper} />

                {/* Default error page */}
                <Route render={() => <NotFound withHeader />} />
            </Switch>

            <PolicyAgreementDialog />
        </React.Fragment>
    )
}

App.propTypes = {
    isLoggedIn: PropTypes.bool.isRequired,
    loadInvites: PropTypes.func.isRequired,
    invites: PropTypes.array,
    redirectUrl: PropTypes.string,
    hasConfirmedEmail: PropTypes.bool,
    hasCompany: PropTypes.bool,
    hasFetchedInvites: PropTypes.bool,
    isSubscribed: PropTypes.bool,
    hasFetchedBillingInfo: PropTypes.bool,
    hasActiveRoles: PropTypes.bool,
    hasFetchedLoggedInUser: PropTypes.bool,
    isLoadingUserInvites: PropTypes.bool,
    isAdmin: PropTypes.bool,
}

App.defaultProps = {
    redirectUrl: "/",
    hasConfirmedEmail: false,
    invites: null,
    hasCompany: true,
    hasFetchedInvites: false,
    isSubscribed: false,
    hasFetchedBillingInfo: false,
    hasActiveRoles: false,
    hasFetchedLoggedInUser: false,
    isLoadingUserInvites: false,
    isAdmin: false,
}

// eslint-disable-next-line import/no-unused-modules
export default compose(
    withLanguage(),
    withErrorBoundary(),
    connect,
    React.memo,
)(App)
